<template>
  <div :class="['checkoutList', !isZkm && 'checkout-list-regular']">
    <form v-scroll-to-error @keydown.enter.prevent>
      <module-title
        :event="event"
        color="#333333"
        :title="$t('registerPaymentVerification')"
        :class="['module-title-card ml-0', !isZkm && 'checkout-title-regular']"
        :font-style="titleStyle"
      />
      <div :class="['container', !isZkm && 'container-regular']">
        <div v-if="user == null" class="zkm-border">
          <div class="subtitle-checkout">{{ $t(isZkm ? 'zkmSignIn' : 'regularSignIn') }}</div>
          <div class="signin-buttons">
            <button
              id="signinDialog"
              class="email-signin-btn email-signin-btn-zkm"
              @click="showSigninDialog = true"
            >
              <div class="btn-text-zkm">{{ $t('emailLogin') }}</div>
            </button>
            <div
              v-if="!isGoogleLoginDisabled"
              :class="[
                'google-signin-wrapper',
                { 'google-signin-wrapper-disabled': isGoogleLoginDisabled },
              ]"
            >
              <p v-if="isGoogleLoginDisabled" class="external-browser-text">
                {{ $t('openInExternalBrowser')
                }}<span style="font-weight: 700">{{ $t('openInExternalBrowserBold') }}</span>
              </p>
              <button id="google-btn" class="google-signin">
                <img src="../../../assets/google-icon-black.svg" alt="Google icon" />
                <span class="google-btn-text">{{ $t('googleLogin') }}</span>
              </button>
            </div>
          </div>
          <div style="margin-top: 1.5rem; margin-left: 0.5rem">
            {{ $t('registerIfNoAccount') }}
            <span class="here-link" @click="showRegistrationDialog = true">{{ $t('here') }}</span>
          </div>
        </div>
        <div v-else-if="!isZkm" class="regular-logged-in">
          <div class="btn-text btn-text-zkm">{{ $t('singedInAs') }}</div>
          <div class="user-info">
            <div class="user-name">
              <img src="../../../assets/round-person.svg" />
              <span class="name-container"> {{ user.firstName }} {{ user.lastName }} </span>
            </div>
            <div class="identity-container">
              <span class="identity-question-container"
                >{{ $t('youAreNot') }} {{ user.firstName }}?</span
              >
              <a id="notYourProfile" style="color: inherit" @click="onLogout">
                <span class="logout-link-container">{{ $t('clickHere') }}</span>
              </a>
            </div>
          </div>
        </div>
        <payment-method
          v-if="!isTotalPriceZero"
          :tickets="attendeeTicket"
          :event="event"
          @paymentMethod="paymentMethod($event)"
        />
        <div class="title-description mb-32 pl-0">
          Unesite podatke o kupcu. Zatim ispunite polja o posjetiteljima.
        </div>
        <div v-if="user !== null && isZkm">
          <div class="profile-title">{{ $t('yourAccount') }}</div>
          <p>{{ $t('singedInAs') }}</p>
          <button class="profile-btn">
            <img class="profile-icon" src="../../../assets/account-icon.svg" />
            <div class="btn-text">{{ user.firstName }} {{ user.lastName }}</div>
          </button>
          <div class="not-profile-container">
            <p>{{ $t('youAreNot') }} {{ user.firstName }}?</p>
            <a id="notYourProfile" class="not-profile" @click="onLogout">
              <p>{{ $t('clickHere') }}</p>
            </a>
          </div>
        </div>
        <div v-show="user">
          <div
            v-if="!hideBuyerFields"
            :class="[
              'subtitle-checkout',
              isZkm ? 'subtitle-store-checkout-zkm' : 'subtitle-store-checkout-regular',
            ]"
          >
            {{ $t('customerData') }}
          </div>
          <checkout-form
            ref="checkoutFormRef"
            :event="event"
            :styles="styles.form"
            :fields="schema.attendeeFields"
            :category="category"
            :user="user"
            :initial-values="initialValues"
            :vs="$v"
            :is-zkm="isZkm"
            :is-gift-card-selected="isGiftCardSelected"
            :total-order-price="totalOrderPrice"
            @values="form = $event"
            @applyGiftCard="applyGiftCard"
          />
        </div>
        <div class="attendee-actions attendee-modals-zkm" :style="{ display: !isZkm && 'none' }">
          <main-button
            class="back-btn"
            flat
            :text="styles.goBack || $t('back')"
            color="#333"
            @click="$emit('back')"
          />

          <main-button
            v-if="event.extraModules.workshops"
            id="continueWorkshop"
            class="continue-btn"
            depressed
            :text="styles.continue || 'Nastavi na radionice'"
            color="#333"
            :disabled="disabledSubmitButton"
            @click="submitForm()"
          />
          <main-button
            v-else
            id="confirmOrder"
            class="continue-btn"
            depressed
            :text="styles.continue || $t('confirmOrder')"
            color="#333"
            :disabled="disabledSubmitButton"
            @click="submitForm()"
          />
        </div>
        <quick-signin-dialog :show.sync="showSigninDialog" @success="refetchUser" />
        <quick-registration-dialog
          :show.sync="showRegistrationDialog"
          @errors="snackbar = $event"
          @success="refetchUser"
        />
      </div>
    </form>
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import ModuleTitle from './../../shared/module-title.vue'
import CheckoutForm from './../checkout-form.vue'
import { submitGuard } from '@/utils'
import PaymentMethod from './payment-method.vue'

import {
  decimal,
  helpers,
  maxLength,
  maxValue,
  minLength,
  minValue,
  required,
  requiredIf,
  sameAs,
} from 'vuelidate/lib/validators'
import QuickSigninDialog from './quick-signin-dialog.vue'
import QuickRegistrationDialog from './quick-registration-dialog.vue'
import { GOOGLE_LOGIN } from '../../../graphql/auth.graphql'
import { logout } from '../../../http'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import { GoogleAuth } from '@/types'
import { mapActions, mapGetters } from 'vuex'

const isSuccessfullyAppliedGiftCard = isSuccessful =>
  helpers.withParams(
    { type: 'isSuccessfullyAppliedGiftCard', value: isSuccessful },
    value => !value || value.length !== 20 || isSuccessful,
  )

export default {
  name: 'checkout',
  components: {
    CheckoutForm,
    ModuleTitle,
    QuickSigninDialog,
    QuickRegistrationDialog,
    PaymentMethod,
    SimpleSnackbar,
  },

  props: {
    event: {
      type: Object,
      default: null,
    },
    category: {
      type: Object,
      default: null,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    schema: {
      type: Object,
      default: () => ({}),
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
    selectedTickets: {
      type: Array,
      default: () => [],
    },
    totalOrderPrice: {
      type: String,
      default: null,
    },
    hideBuyerFields: {
      type: Boolean,
      default: false,
    },
    isGoogleLoginDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSigninDialog: false,
      showRegistrationDialog: false,
      snackbar: {
        open: false,
        messageProp: '',
        color: 'primary',
      },
      form: this.initialValues,
      attendeeTicket: this.initialValues.tickets
        ? this.initialValues.tickets
        : this.selectedTickets.reduce((acc, val) => {
            let values = acc
            values.push({ ticketId: val.id })

            return values
          }, []),
      secondaryColor: '#0277BD',
      paymentType: 'CreditCard',
      colorZkm: '#333333',
      titleStyle: {
        fontFamily: 'Raleway',
        fontSize: '2.25rem',
        fontStyle: 'normal',
        fontWeight: '800',
        lineHeight: '120%',
        letterSpacing: '-0.0675rem',
      },
      isGiftCardSelected: false,
      giftCard: null,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),
    disabledSubmitButton() {
      if (this.isZkm) {
        if (!this.$store.getters['auth/isAuthenticated']) return true
        if (this.hideBuyerFields) return false

        return this.$v.$invalid
      } else {
        if (!this.$store.getters['auth/isAuthenticated']) return true
        if (this.hideBuyerFields) return false

        return this.$v.$invalid
      }
    },
    isTotalPriceZero() {
      if (this.giftCard) {
        return (
          Number(this.totalOrderPrice.split(' ')[1]) - Number(this.giftCard.amount) <= 0 &&
          this.giftCard.isInRange
        )
      }

      return Number(this.totalOrderPrice.split(' ')[1]) <= 0
    },
    isZkm() {
      return this.event.organization.id == process.env.VUE_APP_ZKM_ORGANIZATION_ID
    },
  },
  watch: {
    initialValues: {
      handler() {
        this.form = {
          ...this.form,
          ...this.initialValues,
        }
      },
    },
    'form.paymentType': function (val) {
      if (val === 'GiftCard') {
        this.isGiftCardSelected = true
        if (!this.isGiftCardSelected && !!this.$refs.checkoutFormRef.$data.form.giftCardCode) {
          this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard = false
          this.$refs.checkoutFormRef.$data.form.giftCardCode = ''
          this.$refs.checkoutFormRef.$data.form.giftCardAmount = ''
        }
      }
    },
    selectedTickets: {
      handler() {
        this.attendeeTicket = this.initializeAttendeeTicket()
      },
    },
    user: {
      handler(value) {
        this.$emit('user', value)
        if (this.user == null && !this.isGoogleLoginDisabled) this.initGoogleAuth()
      },
    },
  },
  mounted() {
    if (!this.isGoogleLoginDisabled) {
      this.initGoogleAuth()
    }
  },
  validations() {
    //remove from validation for older events - remove this when events are over
    delete this.schema.validationSchema['Document Id']
    delete this.schema.validationSchema.Function

    return {
      form: {
        ...this.schema.validationSchema,
        acceptTermsAndConditions: {
          sameAs: sameAs(() => true),
        },
        legalEntityName: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityOIB: {
          required: requiredIf(() => this.form.r1Invoice),
          minLength: minLength(7),
          maxLength: maxLength(11),
        },
        legalEntityAddress: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityZipcode: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityCity: { required: requiredIf(() => this.form.r1Invoice) },
        legalEntityCountry: { required: requiredIf(() => this.form.r1Invoice) },
        giftCardCode: {
          required: requiredIf(() => this.isGiftCardSelected),
          minLength: minLength(10),
          maxLength: maxLength(20),
          isSuccessfullyApplied: isSuccessfullyAppliedGiftCard(
            this.form.giftCardCode
              ? this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard
              : '',
          ),
        },
        'OIB / LIB (National Identification Number)':
          Number(this.event.id) === 2953
            ? {
                required,
                minLength: minLength(11),
                maxLength: maxLength(11),
              }
            : {},
        giftCardAmount:
          this.$refs.checkoutFormRef && this.$refs.checkoutFormRef.$data.maxGiftCardAmount
            ? {
                required: requiredIf(() => this.isGiftCardSelected),
                decimal,
                minValue: minValue(0.01),
                maxValue: maxValue(this.$refs.checkoutFormRef.$data.maxGiftCardAmount),
              }
            : {
                required: requiredIf(() => this.isGiftCardSelected),
                decimal,
                minValue: minValue(0.01),
              },
      },
    }
  },
  methods: {
    ...mapActions({
      vuexLogout: 'auth/logout',
      fetchUser: 'auth/fetchUser',
    }),
    paymentMethod(method) {
      if (method === 'GiftCard') {
        this.isGiftCardSelected = !this.isGiftCardSelected
        if (!this.isGiftCardSelected && !!this.$refs.checkoutFormRef.$data.form.giftCardCode) {
          this.$refs.checkoutFormRef.$data.isSuccessfullyAppliedGiftCard = false
          this.$refs.checkoutFormRef.$data.form.giftCardCode = ''
          this.$refs.checkoutFormRef.$data.form.giftCardAmount = ''
        }

        return
      }
      this.paymentType = method
    },
    triggerButtonClick() {
      this.submitForm()
    },
    async onLogout() {
      if (window.gapi && window.gapi.auth2 && window.gapi.auth2.getAuthInstance()) {
        await window.gapi.auth2.getAuthInstance().signOut()
      }
      await logout()
      await this.vuexLogout()
    },
    changeValue(index, event) {
      const ticketId = this.attendeeTicket[index].ticketId
      this.attendeeTicket.splice(index, 1, { ...event, ticketId })
    },
    async submitForm() {
      await submitGuard(this, async () => {
        this.$emit('next', this.connectAttendeeTickets())
      })
    },

    connectAttendeeTickets() {
      let merged = []

      for (let value of this.attendeeTicket) {
        let acc = { ...value }
        merged.push(acc)
      }

      return { tickets: merged, ...this.form, paymentType: this.paymentType }
    },
    initializeAttendeeTicket() {
      let attendeeTicket = this.initialValues.tickets || [this.initialValues.fields]
      const ticketsAmount = this.selectedTickets.reduce((acc, val) => {
        let values = acc
        values[val.id] = values[val.id] ? values[val.id] + 1 : 1

        return values
      }, {})

      const attendees = attendeeTicket.reduce((acc, val) => {
        let values = acc
        values[val.id] = values[val.id] ? values[val.id] + 1 : 1

        return values
      }, {})
      for (const e of Object.keys(ticketsAmount)) {
        if (ticketsAmount[e] < attendees[e] && attendees[e] !== undefined) {
          while (ticketsAmount[e] !== attendees[e]) {
            const index = attendeeTicket.findIndex(item => item.ticketId === e)
            attendeeTicket.splice(index, 1)
            attendees[e]--
          }
        } else if (ticketsAmount[e] > attendees[e] || attendees[e] === undefined) {
          while (ticketsAmount[e] !== attendees[e] || attendees[e] === undefined) {
            attendeeTicket.push({ ticketId: e })
            if (attendees[e] === undefined) {
              attendees[e] = 1
            } else {
              attendees[e]++
            }
          }
        }
      }

      for (const e of Object.keys(attendees)) {
        if (ticketsAmount[e] === undefined) {
          while (attendees[e] !== 0) {
            const index = attendeeTicket.findIndex(item => item.ticketId === e)
            attendeeTicket.splice(index, 1)
            attendees[e]--
          }
          delete attendees[e]
        }
      }

      return attendeeTicket
    },
    initGoogleAuth() {
      window.gapi.load(
        'auth2',
        function () {
          window.gapi.auth2
            .init({
              clientId: GoogleAuth.CLIENT_ID,
              plugin_name: GoogleAuth.PLUGIN_NAME,
            })
            .then(() => {
              window.gapi.auth2
                .getAuthInstance()
                .attachClickHandler('google-btn', {}, this.googleSignIn)
            })
        }.bind(this),
      )
    },
    async googleSignIn(user) {
      const googleTokenId = user.getAuthResponse().id_token
      const { errors } = await this.$apollo.mutate({
        mutation: GOOGLE_LOGIN,
        variables: { googleTokenId },
      })
      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        await this.fetchUser()
      }
    },
    refetchUser() {
      this.fetchUser()
    },
    applyGiftCard(giftCardData) {
      this.$emit('applyGiftCard', giftCardData)
    },
  },
}
</script>

<style scoped>
.signin-buttons {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  @media screen and (max-width: 520px) {
    flex-direction: column;
    gap: 0;
    align-items: center;
  }
}

.google-signin {
  border-radius: 24px;
  background: #f5f5f5;
  border-color: #333333;
  display: flex;
  height: 44px;
  padding: 0.75rem 1.5rem;
  align-items: center;
  gap: 10px;
}

.google-signin-wrapper-disabled {
  display: flex;
  padding: 1rem;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 2rem;
  background: #fff;
  margin-top: 1rem;
}

.google-signin-wrapper-disabled .google-signin {
  border: 1px solid rgba(151, 151, 151, 0.5);
  background: #fff;
}

.google-btn-text {
  font-family: Raleway;
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
}

.zkm-hide {
  display: none;
}

.attendee-actions-zkm {
  background: var(--grey-40, #f5f5f5);
  margin: -24px;
  padding: 24px;
  @media screen and (max-width: 800px) {
    margin: 0;
  }
}

.zkm-border {
  border: solid var(--stroke-one, rgba(151, 151, 151, 0.5));
  border-width: 0 0 1px 0;
  border-radius: 0px;
  margin: 0px -24px;
  padding: 2rem 1.5rem;
  padding-top: 0;
  background: var(--grey-40, #f5f5f5);
}

.zkm-border .subtitle-checkout {
  margin-top: 0;
  padding-top: 2rem;
}

.container {
  border: 1px solid var(--stroke-one, rgba(151, 151, 151, 0.5));
  padding: 24px;
  padding-top: 0;
  border-radius: 6px;
}

.container-regular {
  @media screen and (max-width: 992px) {
    border-left: unset;
    border-right: unset;
    border-radius: unset;
  }
  @media screen and (max-width: 992px) and (min-width: 960px) {
    width: 100%;
  }
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin-top: 5rem;
  }
}

.module-title-card {
  margin-bottom: 2.5rem;
  @media screen and (max-width: 1000px) {
    margin-bottom: 2rem;
  }
}

.back-button-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  width: fit-content;
}

.back-button-icon-container {
  margin-right: 1rem;
  display: flex;
  align-items: center;
}

.back-button-icon {
  height: 1rem;
}

.back-button-text-container {
  font-family: 'Raleway', sans-serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ff4c00;
  line-height: 120%;
  letter-spacing: -0.015em;
}

.subtitle-checkout {
  font-family: Raleway, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 28px;
  color: #333333;
  margin-bottom: 20px;
  margin-top: 30px;
}

.subtitle-checkout-zkm {
  margin-bottom: 3rem;
}

button {
  background: white;
  border: 2px solid #0277bd;
  border-radius: 24px;
  padding: 8px 60px;
  margin-top: 24px;
}

button div {
  color: #0277bd;
  font-weight: bold;
  font-size: 16px;
  font-family: Raleway, 'sans-serif';
  line-height: 100%;
}

p {
  font-family: 'Source sans pro';
  font-size: 16px;
  line-height: 20px;
  color: #666666;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.profile-title {
  font-size: 20px;
  font-weight: 600;
  font-family: Raleway, 'sans-serif';
  margin-bottom: 16px;
}

.profile-btn {
  display: flex;
  border: none;
  background-color: #e6f1f8;
  color: #0277bd;
  border-radius: 24px;
  padding: 12px 14px;
  font-size: 20px;
  font-weight: 700;
  justify-content: space-around;
  align-items: center;
  margin: 16px 0;
  cursor: unset;
}

.attendee-actions {
  display: flex;
  justify-content: flex-end;
}

@media screen and (max-width: 800px) {
  .attendee-actions {
    flex-direction: column;
    max-width: 100%;
  }
}

.email-signin-btn {
  text-transform: uppercase;
  height: 44px;
}

.email-signin-btn-zkm {
  border-color: #333333;
  background: var(--grey-40, #f5f5f5);
  padding: 0.75rem 1.5rem;
}

.btn-text-zkm {
  font-size: 18px;
  color: #333333;
}

.btn-text {
  margin-right: 4px;
  margin-left: 10px;
}

.not-profile-container {
  display: flex;
}

.not-profile {
  margin-left: 4px;
}

@media (max-width: 992px) and (min-width: 801px) {
  .checkoutList {
    padding: 0 2rem;
  }
}

.back-btn {
  border: none !important;
}

.not-profile p {
  color: #0277bd;
  text-decoration: underline;
}

@media screen and (max-width: 800px) {
  .attendee-actions {
    flex-direction: column;
    max-width: 100%;
  }
}

@media (max-width: 992px) and (min-width: 801px) {
  .checkoutList {
    padding: 0 2rem;
  }
}

.back-btn {
  border: none !important;
}

.regular-logged-in {
  display: flex;
  margin: 0 -24px;
  padding: 2rem 1.5rem;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1.5rem;
  align-self: stretch;
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  background: #f5f5f5;

  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.0375rem;
  @media screen and (min-width: 801px) and (max-width: 992px) {
    margin-top: -0.2rem;
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.user-name {
  display: flex;
  height: 2.875rem;
  padding: 0.75rem 1.5rem 0.75rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 1.5rem;
  border: 2px solid #333;
}

.name-container {
  font-variant-numeric: unset;
  font-feature-settings: 'clig' off, 'liga' off;
  font-weight: 700;
  letter-spacing: 0.01688rem;
}

.identity-container {
  display: flex;
  gap: 0.38rem;
}

.identity-question-container {
  font-variant-numeric: unset;
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.015rem;
}

.logout-link-container {
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.015rem;
  border-bottom: 0.5px solid #333;
}

.subtitle-store-checkout-regular {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 2rem;
}

.checkout-title-regular {
  @media screen and (max-width: 992px) {
    display: none;
  }
}

.checkout-list-regular {
  @media screen and (max-width: 992px) and (min-width: 801px) {
    margin: -3.5rem -3.5rem 0 -3.5rem;
  }
  @media screen and (max-width: 800px) {
    margin: 0 -1.5rem 0 -1.5rem;
  }
}

.here-link {
  text-decoration: underline;
  cursor: pointer;
}

.external-browser-text {
  margin: 0;
  color: #666;
  text-align: center;
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
</style>
